import { ScrollBar } from '@components/ScrollBar'
import { FloatingExpandButton } from '@components/FloatingExpandButton'
import { useScrollControl } from '@hooks/useScrollControl'
import { IMedia } from '@dtos/Course'
import { useMediaSize } from '@hooks/useMediaSize'
import { useImageViewerNavigation } from '@hooks/useImageViewerNavigation'
import { View, isWeb } from 'tamagui'
import { useMemo, useState } from 'react'
import { useMediaRenderWrapperDynamicHeight } from '@hooks/useMediaRenderWrapperDynamicHeight'
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native'
import { useLocalSearchParams } from 'expo-router'
import { isDownloadOnlyFileExt } from '@constants/mediaTypes'

interface IMediaRenderWrapperProps {
  children: React.ReactNode
  media: IMedia
  /** Sobrescreve a altura do wrapper (ex.: player de vídeo no web desktop). */
  heightOverride?: number
}

export function MediaRenderWrapper({
  children,
  media,
  heightOverride,
}: IMediaRenderWrapperProps) {
  const { enrollmentId, courseId, courseContentsId } = useLocalSearchParams()
  const mediaSize = useMediaSize()
  const [contentHeight, setContentHeight] = useState(0)
  const { setIsScrollingContent } = useScrollControl()
  const isPdf = media.fileExt?.toLowerCase() === '.pdf'

  const { handleNavigateToImageViewer } = useImageViewerNavigation({
    enrollmentId: enrollmentId?.toString() || '',
    courseId: courseId?.toString() || '',
    courseContentsId: courseContentsId?.toString() || '',
  })

  const dynamicHeight = useMediaRenderWrapperDynamicHeight({
    mediaSize,
    mediaType: media.type,
    isExpanded: false,
    textHeight: contentHeight,
    fileExtension: media.fileExt,
  })
  const mediaRenderWrapperHeight = heightOverride ?? dynamicHeight

  const isAVideo = useMemo(() => {
    return ['Video', 'WebinarHerospark'].includes(media.type ?? '')
  }, [media.type])

  // Vídeo no web sem altura imposta pelo layout (tablet e celular, no arranjo
  // compacto do LessonLayout): o player já se dimensiona por aspect-ratio 16:9, então o
  // wrapper acompanha o filho. Travar a altura aqui num valor por breakpoint
  // fazia o player estourar o `maxHeight` e ficar cortado — o `overflow:
  // hidden` comia justamente a barra de controles nas telas mais altas.
  const fitsChildHeight =
    isWeb && media.type === 'Video' && heightOverride === undefined

  // Planilha e Word entram como mídia full-width: quem preenche a caixa é o
  // card de download do NotSupported, que se dimensiona sozinho.
  const isDownloadOnlyFile = useMemo(
    () => isDownloadOnlyFileExt(media.fileExt),
    [media.fileExt],
  )

  const isFullWidthMedia = useMemo(() => {
    return (
      isAVideo ||
      ['Image', 'Audio'].includes(media.type ?? '') ||
      isDownloadOnlyFile ||
      isPdf
    )
  }, [isAVideo, media.type, isDownloadOnlyFile, isPdf])

  const handleLayout = (event: LayoutChangeEvent) => {
    setContentHeight(event.nativeEvent.layout.height)
  }

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    // Mesma razão do onPressIn/onPressOut abaixo, e o caminho que sobrou de
    // fora daquele fix: no web quem rola a página é o browser, e o
    // `isScrollingContent` vira `overflow: hidden` no container do AppLayout
    // (react-native-web). Como a flag só volta a falso quando a caixa de texto
    // chega ao topo ou ao fim exatos, a página ficava sem barra de rolagem
    // enquanto o aluno lia a aula — HET-4038. O travamento existe para o
    // scroll aninhado do nativo (HET-520).
    if (isWeb) return

    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent
    const isAtTop = contentOffset.y <= 0
    const isAtBottom =
      contentOffset.y + layoutMeasurement.height >= contentSize.height

    setIsScrollingContent(!isAtTop && !isAtBottom)
  }

  // No web o iframe deve permanecer na mesma árvore ao redimensionar. Trocar
  // o pai quando a altura cruza 510px recarrega o documento incorporado.
  const needScrollbar = media.type === 'Text' && (isWeb || contentHeight > 510)
  const isImageMedia = useMemo(() => media.type === 'Image', [media.type])
  const isTextMedia = useMemo(() => media.type === 'Text', [media.type])

  return (
    <View
      height={fitsChildHeight ? 'auto' : mediaRenderWrapperHeight}
      backgroundColor={isFullWidthMedia ? 'transparent' : '$bg-surface-raised'}
      borderRadius="$border-radius-md"
      maxHeight={fitsChildHeight ? undefined : mediaRenderWrapperHeight}
      minHeight={fitsChildHeight ? undefined : mediaRenderWrapperHeight}
      w="100%"
      // Texto entra junto com as mídias full-width: com `flex-start` o bloco de
      // conteúdo encolhe até o filho, então um embed que preenche a largura
      // (`width: '100%'`) não tem contra o que resolver e colapsa. Esticando,
      // o HTML da aula (e o embed dentro dele) acompanha o container do player.
      alignItems={isFullWidthMedia || isTextMedia ? 'unset' : 'flex-start'}
      justifyContent="flex-start"
      padding={isFullWidthMedia ? 0 : '$spacing-inset-xxs'}
      overflow="hidden"
      // No web o scroll da página é do browser; travar via isScrollingContent
      // deixava a página presa (iframe de vídeo/PDF não dispara onPressOut).
      onPressIn={isWeb ? undefined : () => setIsScrollingContent(true)}
      onPressOut={isWeb ? undefined : () => setIsScrollingContent(false)}
      testID="media-render-wrapper"
      position="relative"
      onPress={() => {
        if (isImageMedia && !isWeb) {
          handleNavigateToImageViewer()
        }
      }}
    >
      {needScrollbar ? (
        <ScrollBar
          scrollBarId="media-render-wrapper"
          displayNativeIndicator={false}
          maxHeight={mediaRenderWrapperHeight - 16}
          onScroll={handleScroll}
          fillContentWidth
          // A barra ocupa largura: sem espaço reservado, um embed responsivo
          // encolhe ao mostrá-la, perde o overflow e cresce ao escondê-la,
          // repetindo o ciclo indefinidamente (HET-4197).
          reserveScrollBarSpace={isWeb}
        >
          <View
            onLayout={handleLayout}
            paddingRight="$spacing-inset-xxs"
            w="100%"
          >
            {children}
          </View>
        </ScrollBar>
      ) : (
        <View
          onLayout={handleLayout}
          paddingRight={isTextMedia ? '$spacing-inset-xxs' : 0}
          w={isTextMedia ? '100%' : undefined}
        >
          {children}
        </View>
      )}

      {isImageMedia && !isWeb && (
        <FloatingExpandButton onPress={handleNavigateToImageViewer} />
      )}
    </View>
  )
}
